import { type JSX, useState } from 'react';
import { LuCheck, LuHouse, LuPencil, LuX } from 'react-icons/lu';

import type { EventView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { getHouseholdName } from 'utils';
import { useCommandCenterHouseholds } from 'views/household/household-queries';

import { useAssignEventHousehold } from './events-queries';

interface EventDetailsHouseholdSectionProps {
    event: EventView;
    readOnly?: boolean;
}

export function EventDetailsHouseholdSection({
    event,
    readOnly = false,
}: EventDetailsHouseholdSectionProps): JSX.Element {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedHouseholdGUID, setSelectedHouseholdGUID] = useState(event.clientHouseholdGUID ?? '');

    const { data: households } = useCommandCenterHouseholds();
    const { mutate: assignHousehold, isPending } = useAssignEventHousehold();

    const householdOptions: ComboboxOption[] =
        households?.map((h) => ({
            value: h.globalAccountGUID,
            label: getHouseholdName(h) || 'Unnamed Household',
        })) ?? [];

    const currentHouseholdLabel = householdOptions.find((o) => o.value === event.clientHouseholdGUID)?.label ?? null;

    const handleConfirm = () => {
        if (!selectedHouseholdGUID || selectedHouseholdGUID === event.clientHouseholdGUID) {
            setIsEditing(false);
            return;
        }

        assignHousehold(
            { eventGUID: event.eventGUID, householdGUID: selectedHouseholdGUID },
            {
                onSuccess: () => setIsEditing(false),
            },
        );
    };

    const handleCancel = () => {
        setSelectedHouseholdGUID(event.clientHouseholdGUID ?? '');
        setIsEditing(false);
    };

    return (
        <div className="flex items-start gap-3">
            <LuHouse className="text-muted-foreground mt-0.5 h-4 w-4" />
            <div className="min-w-0 flex-1">
                <div className="mb-1 text-sm font-medium">Household</div>
                {isEditing ? (
                    <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <Combobox
                                options={householdOptions}
                                value={selectedHouseholdGUID}
                                onValueChange={setSelectedHouseholdGUID}
                                placeholder="Select a household"
                                selectOptionLabel="Select a household"
                                noOptionsLabel="No households found"
                                popoverContentClassName="z-[90]"
                            />
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 rounded-full text-green-600 hover:text-green-700"
                            onClick={handleConfirm}
                            disabled={isPending || !selectedHouseholdGUID}
                            aria-label="Confirm household"
                        >
                            <LuCheck className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground h-8 w-8 shrink-0 rounded-full hover:text-red-600"
                            onClick={handleCancel}
                            disabled={isPending}
                            aria-label="Cancel editing"
                        >
                            <LuX className="h-4 w-4" />
                        </Button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">{currentHouseholdLabel ?? 'Not assigned'}</span>
                        {!readOnly && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground h-6 w-6 shrink-0 rounded-full"
                                onClick={() => setIsEditing(true)}
                                aria-label="Edit household"
                            >
                                <LuPencil className="h-3 w-3" />
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
